/**
 * Encryption utilities for securing sensitive integration data
 * Uses Web Crypto API with AES-GCM for authenticated encryption
 */

/**
 * Derives an encryption key from a password using PBKDF2
 * The password is derived from the user's browser fingerprint + a static salt
 */
async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Generates a deterministic password based on browser fingerprint
 * This allows us to encrypt data at rest without requiring user passwords
 */
async function getBrowserFingerprint(): Promise<string> {
  // Combine various browser characteristics for a fingerprint
  const components = [
    navigator.userAgent,
    navigator.language,
    new Date().getTimezoneOffset().toString(),
    screen.colorDepth.toString(),
    screen.width.toString(),
    screen.height.toString(),
  ];

  const fingerprintData = components.join('|');
  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprintData);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Encrypts a string using AES-GCM
 * Returns base64-encoded encrypted data with IV prepended
 */
export async function encryptString(plaintext: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plaintext);

  // Generate a random IV (Initialization Vector)
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Use browser fingerprint as password (with static salt for consistency)
  const salt = new Uint8Array([
    0x73, 0x68, 0x61, 0x6b, 0x65, 0x73, 0x70, 0x65,
    0x61, 0x72, 0x65, 0x2e, 0x64, 0x69, 0x79, 0x21,
  ]); // "shakespeare.diy!"
  const password = await getBrowserFingerprint();
  const key = await deriveKey(password, salt);

  // Encrypt the data
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    data
  );

  // Combine IV + encrypted data
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);

  // Return as base64
  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypts a string encrypted with encryptString
 */
export async function decryptString(encryptedBase64: string): Promise<string> {
  // Decode from base64
  const combined = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));

  // Extract IV and encrypted data
  const iv = combined.slice(0, 12);
  const encrypted = combined.slice(12);

  // Use same password derivation as encryption
  const salt = new Uint8Array([
    0x73, 0x68, 0x61, 0x6b, 0x65, 0x73, 0x70, 0x65,
    0x61, 0x72, 0x65, 0x2e, 0x64, 0x69, 0x79, 0x21,
  ]);
  const password = await getBrowserFingerprint();
  const key = await deriveKey(password, salt);

  // Decrypt the data
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    encrypted
  );

  // Decode as UTF-8
  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}

/**
 * Encrypts an object by encrypting its JSON representation
 */
export async function encryptObject<T>(obj: T): Promise<string> {
  const json = JSON.stringify(obj);
  return encryptString(json);
}

/**
 * Decrypts an object that was encrypted with encryptObject
 */
export async function decryptObject<T>(encryptedBase64: string): Promise<T> {
  const json = await decryptString(encryptedBase64);
  return JSON.parse(json);
}
